import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const POLICY_CONTROL_DEFAULTS = {
  who_can_create: "admin",
  creation_approval: "owner",
  who_can_edit: "creator_admin",
  edit_condition: "approval",
};

const CONTROLS = [
  {
    key: "who_can_create",
    label: "Who can create a policy?",
    desc: "Sets the minimum role allowed to draft new policies with the Policy Builder.",
    options: [
      { value: "owner", label: "Owner only" },
      { value: "admin", label: "Admins & Owner" },
      { value: "member", label: "Members and above" },
      { value: "anyone", label: "Anyone in the workspace" },
    ],
  },
  {
    key: "creation_approval",
    label: "What approvals do new policies need?",
    desc: "Before a newly created policy starts enforcing on expenses.",
    options: [
      { value: "none", label: "None — live immediately" },
      { value: "admin", label: "One Admin must approve" },
      { value: "owner", label: "Owner must approve" },
    ],
  },
  {
    key: "who_can_edit",
    label: "Who can edit a policy?",
    desc: "Controls who can change conditions, severity or exceptions on existing policies.",
    options: [
      { value: "owner", label: "Owner only" },
      { value: "admin", label: "Admins & Owner" },
      { value: "creator_admin", label: "Policy creator + Admins" },
      { value: "anyone", label: "Anyone in the workspace" },
    ],
  },
  {
    key: "edit_condition",
    label: "Under what conditions can edits happen?",
    desc: "Guardrails applied whenever an existing policy is modified.",
    options: [
      { value: "anytime", label: "Anytime — changes apply immediately" },
      { value: "disabled_only", label: "Only while the policy is disabled" },
      { value: "approval", label: "Edits require re-approval" },
    ],
  },
];

export const PolicyControls = () => {
  const [controls, setControls] = useState(null);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setControls({ ...POLICY_CONTROL_DEFAULTS, ...(r.data.policy_controls || {}) }))
      .catch(() => setControls(POLICY_CONTROL_DEFAULTS));
  }, []);

  const update = async (key, value) => {
    const next = { ...controls, [key]: value };
    setControls(next);
    try {
      await api.put("/settings", { policy_controls: next });
      toast.success("Policy controls updated");
    } catch {
      toast.error("Could not save policy controls");
    }
  };

  if (!controls) return <div className="py-6 text-center text-[12px] text-[#555]">Loading controls…</div>;

  return (
    <div className="space-y-3" data-testid="policy-controls">
      {CONTROLS.map((c) => (
        <div key={c.key} className="rounded-xl border border-[#1e1e1e] bg-[#141414] px-4 py-3" data-testid={`policy-control-${c.key}`}>
          <div className="text-[12.5px] font-medium text-white">{c.label}</div>
          <div className="mt-0.5 text-[10.5px] text-[#6b6b6b]">{c.desc}</div>
          <Select value={controls[c.key]} onValueChange={(v) => update(c.key, v)}>
            <SelectTrigger className="mt-2 h-8 border-[#2a2a2a] bg-[#161616] text-[12px]" data-testid={`policy-control-select-${c.key}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-[#222] bg-[#161616] text-white">
              {c.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}
      <p className="px-1 text-[10.5px] leading-relaxed text-[#6b6b6b]">
        You're signed in as Ava Thompson (Owner) — these controls apply to everyone else in Acme Corp.
      </p>
    </div>
  );
};
